import React, { useEffect, useState, useCallback } from "react";

import { AuthUser, onAuthStateChanged } from "./auth/firebase";

interface DelightedSurvey {
  survey: (data: DelightedSurveyData) => void;
}

interface DelightedSurveyData {
  email?: string;
  name?: string;
}

interface Window {
  delighted?: DelightedSurvey;
}

declare var window: Window;

const DELIGHTED_SCRIPT = `
  !function(e,t,r,n){if(!e[n]){for(var a=e[n]=[],i=["survey","reset","config","init","set","get","event","identify","track","page","screen","group","alias"],s=0;s<i.length;s++){var c=i[s];a[c]=a[c]||function(e){return function(){var t=Array.prototype.slice.call(arguments);a.push([e,t])}}(c)}a.SNIPPET_VERSION="1.0.1";var o=t.createElement("script");o.type="text/javascript",o.async=!0,o.src="https://d2yyd1h5u9mauk.cloudfront.net/integrations/web/v1/library/"+r+"/"+n+".js";var p=t.getElementsByTagName("script")[0];p.parentNode.insertBefore(o,p)}}(window,document,"ro6YpFJfXDunAz60","delighted");
`;

const MIN_ACCOUNT_AGE_DAYS = 180; // Number of days (6 months) to wait before showing first survey

const useFirebaseAuth = (): AuthUser | null => {
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    const unregisterPromise = onAuthStateChanged(async (firebaseUser) => {
      setUser(firebaseUser);
    });

    return () => {
      unregisterPromise.then((unregister) => unregister());
    };
  }, []);

  return user;
};

const shouldShowSurvey = (user: AuthUser): boolean => {
  if (!user.metadata.creationTime) return false;

  // Don't show the survey to internal users unless delighted is in test mode
  const userDomain = user.email?.split("@")[1];
  if (userDomain === "freshpaint.io") {
    const queryParams = new URLSearchParams(document.location.search);
    return queryParams.get("delighted") === "test";
  }

  const creationDate = new Date(user.metadata.creationTime);
  const now = new Date();
  // Convert milliseconds to days: 1000ms * 60s * 60min * 24hr = 1 day
  const daysSinceCreation =
    (now.getTime() - creationDate.getTime()) / (1000 * 60 * 60 * 24);

  return daysSinceCreation >= MIN_ACCOUNT_AGE_DAYS;
};

const useDelightedScript = (): void => {
  useEffect(() => {
    const script = document.createElement("script");
    script.innerHTML = DELIGHTED_SCRIPT;
    document.head.appendChild(script);

    const style = document.createElement("style");
    style.textContent = `
      .delighted-web-survey-powered {
        display: none !important;
        opacity: 0 !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(script);
      document.head.removeChild(style);
    };
  }, []);
};

const useSurveyTrigger = (user: AuthUser | null) => {
  const triggerSurvey = useCallback(() => {
    if (!window.delighted?.survey) {
      console.error("Delighted survey function not available");
      return;
    }

    if (!user || !shouldShowSurvey(user)) {
      return;
    }

    const surveyData: DelightedSurveyData = {
      email: user?.email || undefined,
      name: user?.displayName || undefined,
    };

    try {
      window.delighted.survey(surveyData);
    } catch (error) {
      console.error("Error triggering Delighted survey:", error);
    }
  }, [user]);

  useEffect(() => {
    if (user !== null) {
      triggerSurvey();
    }
  }, [user, triggerSurvey]);
};

const DelightedNPS: React.FC = () => {
  const user = useFirebaseAuth();
  useDelightedScript();
  useSurveyTrigger(user);

  return null;
};

export default DelightedNPS;
